import { ChangeEvent, KeyboardEvent, forwardRef, useCallback } from "react";

import { LoadingGroup } from "capitalrx-components-2.0/lib/general";
import { Body } from "capitalrx-components-2.0/lib/typography";
import classNames from "classnames";

import { useId } from "../../hooks/useId";
import { getBasePropsFromHtmlProps } from "../../utilities/getBasePropsFromHtmlProps";
import { getHtmlPropsFromBaseProps } from "../../utilities/getHtmlPropsFromBaseProps";
import { useInputDisabled, useInputReadonly } from "../InputGroup";
import { useSubmitting } from "../SubmitGroup";
import { useValidity } from "../ValidityGroup";
import { InputContainer } from "../shared";
import { NumberTextboxProps } from "./NumberTextbox";
import { Placeholder } from "./Placeholder";
import { StyledNativeInput } from "./StyledNativeInput";

import styles from "./styles.module.scss";

/**
 * The `Input` component properties.
 */
export interface InputProps extends NumberTextboxProps {}

/**
 * The base input for number textboxes.
 */
export const Input = forwardRef<HTMLInputElement, InputProps>(function Input(props, ref) {
  const {
    label,
    tooltip,
    instructions,
    hideInstructions,
    hideLabel,
    defaultValue = null,
    required = false,
    autoFocus,
    placeholder,
    tabIndex,
    step = 1,
    min,
    max,
    list,
    inputMode = "numeric",
    onChange,
    onFocusEnter,
    onFocusExit,
    onEnter,
  } = props;

  const { value = defaultValue } = props;

  const baseHtmlProps = getHtmlPropsFromBaseProps(props, {
    className: [styles.Textbox, "number-textbox"],
    testIdPrefix: "number-textbox",
  });

  const id = useId();
  const listId = list?.length ? `${id}-list` : undefined;
  const instructionsId = instructions ? `${id}-instructions` : undefined;
  const errorId = `${id}-error`;
  const labelId = `${id}-label`;

  const disabled = useInputDisabled(props);
  const readOnly = useInputReadonly(props);
  const submitting = useSubmitting();
  const [invalid] = useValidity();

  const changeHandler = useCallback(
    (ev: ChangeEvent<HTMLInputElement>) => {
      if (disabled || readOnly || submitting) return;

      const rawValue = ev.target.value;
      const numericValue = rawValue === "" ? null : Number(rawValue);

      onChange?.(numericValue, value);
    },
    [value, disabled, readOnly, submitting, onChange],
  );

  const keyDownHandler = useCallback(
    (ev: KeyboardEvent<HTMLInputElement>) => {
      if (ev.key === "Enter" && onEnter) {
        ev.preventDefault();
        onEnter(value);
      }
    },
    [value, onEnter],
  );

  const focusHandler = useCallback(() => {
    onFocusEnter?.();
  }, [onFocusEnter]);

  const blurHandler = useCallback(() => {
    onFocusExit?.();
  }, [onFocusExit]);

  const ariaDescribedBy = [instructionsId, invalid ? errorId : undefined].filter(Boolean).join(" ") || undefined;

  const getAriaLabel = () => {
    if (!hideLabel) return undefined;
    if (typeof label === "string") return label;
    return "Number input";
  };

  return (
    <LoadingGroup.Placeholder fallback={<Placeholder className={props.className} style={props.style} hasInstructions={!!instructions} />}>
      <InputContainer
        {...getBasePropsFromHtmlProps(baseHtmlProps)}
        id={id}
        label={label}
        instructions={instructions}
        disabled={disabled}
        readOnly={readOnly}
        required={required}
        tooltip={tooltip}
        hideInstructions={hideInstructions}
        hideLabel={hideLabel}
      >
        <Body className="input-container">
          <StyledNativeInput
            aria-busy={submitting || undefined}
            aria-describedby={ariaDescribedBy}
            aria-errormessage={invalid ? errorId : undefined}
            aria-invalid={invalid || undefined}
            aria-label={getAriaLabel()}
            aria-labelledby={!hideLabel ? labelId : undefined}
            aria-readonly={readOnly || submitting}
            aria-required={required || undefined}
            autoFocus={autoFocus}
            className={classNames("input", { invalid })}
            data-testid={baseHtmlProps["data-testid"]}
            disabled={disabled}
            id={id}
            inputMode={inputMode}
            list={listId}
            max={max}
            min={min}
            onBlur={blurHandler}
            onChange={changeHandler}
            onKeyDown={keyDownHandler}
            onFocus={focusHandler}
            placeholder={placeholder}
            readOnly={readOnly || submitting}
            ref={ref}
            step={step}
            tabIndex={disabled || readOnly || submitting ? -1 : (tabIndex ?? 0)}
            type="number"
            value={value ?? ""}
          />
          {listId && (
            <datalist id={listId}>
              {list!.map((option) => (
                <option key={option} value={option} />
              ))}
            </datalist>
          )}
        </Body>
      </InputContainer>
    </LoadingGroup.Placeholder>
  );
});
